import React, { useMemo } from "react";
import { Dialog } from "@mhome/ui";
import { useHistory, useLocation } from "react-router-dom";

/**
 * MDialog - Fullscreen Dialog component based on URL hash
 *
 * @param {string} hash - Dialog hash path, e.g. "detail-card" becomes "#detail-card"
 * @param {boolean} fullScreen - Whether to display in fullscreen (default: true)
 * @param {function} onClose - Callback when dialog closes (optional)
 * @param {object} children - Dialog content
 * @param {object} ...otherProps - Other props passed to Dialog
 */
const MDialog = ({
  hash,
  fullScreen = true,
  onClose,
  children,
  open: openProp,
  ...otherProps
}) => {
  const history = useHistory();
  const location = useLocation();

  // Calculate open state directly from hash or use provided open prop
  const open = useMemo(() => {
    // If open prop is provided (for embedded dialogs), use it
    if (openProp !== undefined) {
      return openProp;
    }
    // Otherwise, calculate from hash
    if (!hash) return false;
    const currentHash = location.hash ? location.hash.replace("#", "") : "";
    const targetHash = hash.startsWith("#") ? hash.replace("#", "") : hash;
    return currentHash === targetHash;
  }, [location.hash, hash, openProp]);

  const handleClose = (event, reason) => {
    // If closed by backdrop click or ESC, trigger browser back
    if (reason === "backdropClick" || reason === "escapeKeyDown") {
      const newPath = location.pathname + location.search;
      if (history.length > 1 && location.hash) {
        history.goBack();
      } else {
        history.replace(newPath);
      }
      if (onClose) {
        onClose(event, reason);
      }
    } else if (onClose) {
      onClose(event, reason);
    }
  };

  // Handle manual close (e.g., close button click)
  const handleManualClose = (event) => {
    const newPath = location.pathname + location.search;
    if (history.length > 1 && location.hash) {
      history.goBack();
    } else {
      history.replace(newPath);
    }
    if (onClose) {
      onClose(event);
    }
  };

  const processChildren = (children) => {
    return React.Children.map(children, (child) => {
      if (!React.isValidElement(child)) {
        return child;
      }

      const childProps = {
        ...child.props,
        onClose: handleManualClose,
      };
      if (child.props.children) {
        childProps.children = processChildren(child.props.children);
      }

      return React.cloneElement(child, childProps);
    });
  };

  const processedChildren =
    React.Children.count(children) > 0 ? processChildren(children) : children;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullScreen={fullScreen}
      {...otherProps}
    >
      {processedChildren}
    </Dialog>
  );
};

export default MDialog;

/**
 * useMDialog - Hook to open MDialog
 *
 * @param {string} hash - Dialog hash path
 * @returns {function} openDialog - Function to open the dialog
 */
export const useMDialog = (hash) => {
  const history = useHistory();
  const location = useLocation();

  const openDialog = () => {
    const targetHash = hash.startsWith("#") ? hash : `#${hash}`;
    const currentPath = location.pathname + location.search;
    const newPath = currentPath + targetHash;
    history.push(newPath);
  };

  return openDialog;
};
